import { DownOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Dropdown, Menu, Space } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as permissionKind from '../../../../shared/permissions';
import { type UserSpecsFragment } from '../../../api/fragments/UserSpecs';
import { useAccount } from '../../../components/contexts/AccountContextManager';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import hasPermissions from '../../../utilities/hasPermissions';
import { useDeleteUserConfirmation } from './DeleteUser';

export type UserActionsProps = { user: UserSpecsFragment };

const UserActions = ({ user }: UserActionsProps) => {
    const { t } = useTranslation(['userDetails']);
    const { permissions: accountPermissions } = useAccount();
    const company = useCompany(true);

    const hasManagePermission = company
        ? hasPermissions(company.permissions, [permissionKind.manageUsers]) &&
          hasPermissions(accountPermissions, [permissionKind.updateUser])
        : hasPermissions(accountPermissions, [permissionKind.updateUser]);

    const deleteUserPopUp = useDeleteUserConfirmation(user);
    const navigate = useNavigate();

    const items = useMemo(
        () =>
            [
                hasManagePermission && {
                    label: t('userDetails:mainActionButtons.updateSpecs'),
                    key: 'updateSpecs',
                    icon: <EditOutlined />,
                    onClick: () => navigate(`/admin/accesses/users/${user.id}/update`),
                },
                hasManagePermission && {
                    label: t('userDetails:mainActionButtons.delete'),
                    key: 'delete',
                    icon: <DeleteOutlined />,
                    onClick: deleteUserPopUp.open,
                    danger: true,
                },
            ].filter(Boolean),
        [deleteUserPopUp.open, hasManagePermission, navigate, t, user.id]
    );

    if (!items.length) {
        return null;
    }

    return (
        <>
            <Dropdown overlay={<Menu items={items} />}>
                <Button type="primary">
                    <Space>
                        <span>{t('userDetails:mainActionButtons.actions')}</span>
                        <DownOutlined />
                    </Space>
                </Button>
            </Dropdown>
            {deleteUserPopUp.render()}
        </>
    );
};

export default UserActions;
